<template>
  <div class="gam-more" v-on-click-outside="closeMoreMenu">
    <GamButton v-if="!label" v-bind="getMoreBtn" />

    <div v-else :class="['label', size]" v-bind="getMoreLabel">
      <GamIcon v-if="icon" :name="icon" :size="isMobile ? GamIconSize.SMALL : GamIconSize.NORMAL" /> {{ label }}
    </div>

    <div
      v-if="items"
      :class="['gam-more-container', position, size, { recurrences: isRecurrenceMenu, 'is-more-open': isMoreOpen }]"
    >
      <ScrollOverflow :class="['item-container', props.variant]" is-vertical>
        <GamButton
          v-for="(item, i) in items"
          :key="`m-item-${i}`"
          v-bind="getItemBtn(item)"
          :data-test-id="item.data?.testId || item.label"
          :class="['item', { 'active-more-item': item.isDisabled }]"
          container-class="more-container"
          :style="`--button-full-width: ${fullWidth ? `${fullWidth}px` : 'auto'}`"
        >
          <div class="item-label">
            {{ localize(item.label) }}
            <span v-if="item.asideIcon || item.asideLabel" class="aside">
              <GamIcon v-if="item.asideIcon" :name="item.asideIcon" :size="GamIconSize.SMALL" />
              <span v-if="item.asideLabel">{{ localize(item.asideLabel) }}</span>
            </span>
          </div>
        </GamButton>
      </ScrollOverflow>
    </div>
  </div>
</template>

<script setup lang="ts">
import { localize } from '@/core/gambits';
import { isMobile } from '@/core/helpers/ui.helper';
import GamButton from '@/views/components/GamButton.vue';
import { GamButtonSize, GamButtonVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import type { GamButtonType } from '@/views/composables/models/components/GamButton';
import type { GamMoreItem, GamMoreType } from '@/views/composables/models/components/GamMore';
import { vOnClickOutside } from '@vueuse/components';
import { computed, nextTick, ref, watch } from 'vue';
import GamIcon from '../../GamIcon.vue';
import ScrollOverflow from '../../ScrollOverflow.vue';

const props = defineProps<GamMoreType>();
const isMoreOpen = ref<boolean>(false);

const closeMoreMenu = () => {
  isMoreOpen.value = false;
};

const getItemBtn = (item: GamMoreItem): GamButtonType => {
  // eslint-disable-next-line  @typescript-eslint/no-unused-vars
  const { label, data, ...rest } = item;

  return {
    ...rest,
    isFullSize: true,
    variant: item.variant || GamButtonVariant.TERTIARY,
    size: item.size || GamButtonSize.ACTIONS,
    onClick: (e: MouseEvent) => {
      const result = item.onClick?.(e) as any as number;
      if (result === -1) return;
      isMoreOpen.value = false;
    },
  };
};

const getMoreIcon = (): GamIconName => {
  if (props.size === GamButtonSize.X_SMALL) {
    return GamIconName.DOTS_V;
  } else {
    return isMoreOpen.value ? GamIconName.X_CLOSE : GamIconName.PLUS;
  }
};

const getMoreBtn = computed((): GamButtonType => {
  return {
    isIconOnly: true,
    leftIcon: props.icon || getMoreIcon(),
    size: props.size,
    variant: props.variant ? props.variant : GamButtonVariant.STEKLO,
    isIconWhite: props.isIconWhite,
    isActive: isMoreOpen.value,
    data: { testId: props.data?.testId ?? 'more' },
    onClick: () => {
      isMoreOpen.value = !isMoreOpen.value;
    },
  };
});

const getMoreLabel = computed(() => ({
  onClick: () => {
    isMoreOpen.value = !isMoreOpen.value;
  },
}));

watch(isMoreOpen, () => {
  nextTick(() => {
    const el = document.querySelector('.active-more-item');
    if (!el) return;

    // @ts-ignore
    typeof el.scrollIntoViewIfNeeded === 'function' ? el.scrollIntoViewIfNeeded() : el.scrollIntoView();
  });
});
</script>

<style scoped lang="scss">
@use '@/ui/css/mixins' as *;
@use '@/ui/css/partial';

.gam-more {
  position: relative;
  z-index: 3;
}

.items-more {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-xs);
}

.label {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  text-transform: uppercase;
  padding: var(--spacing-xs) var(--spacing-md);
  border: 1px solid var(--color-white-20);
  transition:
    border var(--transition),
    width 256ms ease-out;
  border-radius: 1.5rem;
  font-size: 13px;
  line-height: 14px;
  letter-spacing: 0.03em;
  font-weight: 400;
  user-select: none;
  position: relative;
  z-index: 3;

  cursor: pointer;

  &:before {
    background: var(--color-linear-gradient);
    opacity: 0.7;
    inset: -1px;
  }

  @include hover {
    background-color: var(--color-dark-600);
    &:before {
      opacity: 1;
    }
  }
}

.label.tiny {
  font-size: 12px;
  padding: var(--spacing-xxs) var(--spacing-s);
}

button {
  letter-spacing: 0.03em;
}

.gam-more-container {
  position: absolute;
  min-width: 144px;
  width: max-content;
  border-radius: var(--radius-small);
  border-radius: 12px;
  border: 1px solid var(--color-white-15);
  overflow: hidden;
  box-shadow: 0px 0px var(--radius-small) color-mix(in srgb, var(--color-dark-1000) 50%, transparent);
  z-index: 2;
  -webkit-backdrop-filter: blur(15px);
  backdrop-filter: blur(15px);
}

.item-container {
  display: flex;
  flex-direction: column;
  padding: var(--spacing-md);
  max-height: 236px;
}

.item-label {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-l);
}

.aside {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 4px;
  span {
    color: var(--color-white-100) !important;
  }
}

.gam-more-container {
  transition: opacity var(--transition);

  .item-container {
    transition: background-color var(--transition-router);
    padding-left: 0;
    padding-right: 0;
  }

  &:not(.is-more-open) {
    opacity: 0;
    pointer-events: none;

    .item-container {
      &,
      &.steklo,
      &.secondary {
        background-color: var(--color-dark-50);
      }

      &.slab {
        background-color: var(--color-white-1);
      }
    }
  }

  &.is-more-open {
    opacity: 1;

    .item-container {
      &,
      &.steklo,
      &.secondary {
        background-color: var(--color-white-5);
      }

      &.slab {
        background-color: var(--color-dark-50);
      }
    }
  }
}

.actions,
.small {
  &.left-below,
  &.right-below {
    top: 38px;
  }

  &.left-above,
  &.right-above {
    bottom: 38px;
  }
}

.large {
  &.left-below,
  &.right-below {
    top: 60px;
  }

  &.left-above,
  &.right-above {
    bottom: 60px;
  }
}

.left-below {
  right: -1px;
  top: 28px;
}

.left-above {
  right: -1px;
  bottom: 28px;
}

.right-below {
  left: -1px;
  top: 28px;
}

.right-above {
  left: -1px;
  bottom: 28px;
}

.recurrences {
  &.left-below,
  &.right-below {
    top: 36px;
  }

  &.left-above,
  &.right-above {
    bottom: 32px;
  }
}

.recurrences.tiny {
  &.left-below,
  &.right-below {
    top: 36px;
  }

  &.left-above,
  &.right-above {
    bottom: 26px;
  }
}

.active-more-item {
  .item-label {
    color: var(--color-pink);
  }
}
</style>

<style>
.gam-button.item .gam-button-container.more-container {
  min-height: 26px;
}
</style>
